import { PopoverOpenButton } from "@msys/ui";
import { KeyboardArrowDown as KeyboardArrowDownIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  FilledInput,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import React from "react";

export type FilterDropdownOption = { value: string; label: string };

interface Props {
  label: string;
  searchLabel: string;
  noOptionsLabel: string;
  options: FilterDropdownOption[];
  value: string[];
  onChange: (value: string[]) => void;
  width?: number;
}

/**
 * Templates-style multi-select filter dropdown for the change log — a labelled
 * dropdown button (mirroring `@msys/ui`'s `ButtonSelectMultiple` look) opening a
 * checkbox popover. Unlike the stock `ButtonSelectMultiple` — which hides its
 * search box for lists under 10 options — this keeps the type-to-filter search
 * **always visible** (the change log's option lists are usually short but search is
 * wanted regardless). Multi-select via checkboxes; the popover stays open while
 * selecting.
 */
export function ChangeLogFilterDropdown({
  label,
  searchLabel,
  noOptionsLabel,
  options,
  value,
  onChange,
  width = 320,
}: Props) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? options.filter(o => o.label.toLowerCase().includes(q)) : options;
  }, [options, search]);

  const toggle = (v: string) =>
    onChange(value.includes(v) ? value.filter(x => x !== v) : [...value, v]);

  return (
    <PopoverOpenButton
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false);
        setSearch("");
      }}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      content={
        <Stack direction="column" spacing={0} sx={{ width }}>
          <Box sx={{ p: 1, borderBottom: 1, borderColor: "divider" }}>
            <FilledInput
              size="extra-small"
              type="search"
              fullWidth
              autoFocus
              placeholder={`${searchLabel}…`}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </Box>
          {filtered.length === 0 ? (
            <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
              {noOptionsLabel}
            </Typography>
          ) : (
            <List dense sx={{ maxHeight: 320, overflowY: "auto", py: 0 }}>
              {filtered.map(o => (
                <ListItemButton
                  key={o.value}
                  dense
                  onClick={() => toggle(o.value)}
                >
                  <Checkbox
                    edge="start"
                    size="small"
                    checked={value.includes(o.value)}
                    tabIndex={-1}
                    disableRipple
                  />
                  <ListItemText
                    primary={o.label}
                    slotProps={{ primary: { variant: "body2" } }}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Stack>
      }
    >
      <Button
        size="extra-small"
        variant="text"
        color="primary"
        sx={{ textTransform: "none" }}
        endIcon={
          <KeyboardArrowDownIcon
            sx={{
              transition: "transform 0.2s ease-out",
              ...(open ? { transform: "rotate(-180deg)" } : undefined),
            }}
          />
        }
      >
        {label}
        {value.length > 0 ? ` (${value.length})` : ""}
      </Button>
    </PopoverOpenButton>
  );
}
